import React, { useMemo } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import ChartDataLabels from "chartjs-plugin-datalabels"; // <- added plugin

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  ChartDataLabels // <- register datalabels plugin
);

const DEFAULT_GROUPS = [
  {
    name: "Neonatal Deaths (<=4w)",
    data: [{ Sepsis: 159 }, { Asphyxia: 299 }, { "Other Causes": 160 }],
  },
  {
    name: "Infant Deaths (1 -12 Months)",
    data: [{ Pneumonia: 71 }, { Diarrhea: 3 }, { "Fever Related": 20 }, { Measles: 0 }, { Other: 722 }],
  },
  {
    name: "Child Deaths (1-5 Years)",
    data: [{ Pneumonia: 55 }, { Diarrhea: 42 }, { "Fever Related": 45 }, { Measles: 19 }, { Other: 964 }],
  },
];

const BAR_COLORS = ["#5e72e4", "#11cdef", "#fb6340", "#2dce89", "#8965e0"];

export default function DeathsGroupedChart({ groups = DEFAULT_GROUPS, selectedDistrict = null, height = 320 }) {
  const scaledGroups = useMemo(() => {
    if (!selectedDistrict) return groups;
    return groups.map((g) => ({
      ...g,
      data: g.data.map((d) => {
        const key = Object.keys(d)[0];
        return { [key]: Math.round(d[key] * 0.2) };
      }),
    }));
  }, [groups, selectedDistrict]);

  const { labels, matrix } = useMemo(() => {
    const labelSet = new Set();
    scaledGroups.forEach((g) => (g.data || []).forEach((obj) => labelSet.add(Object.keys(obj)[0])));
    const labels = Array.from(labelSet);

    const matrix = scaledGroups.map((g) => {
      const rowMap = {};
      (g.data || []).forEach((obj) => {
        const k = Object.keys(obj)[0];
        rowMap[k] = Number(Object.values(obj)[0]) || 0;
      });
      return labels.map((lab) => rowMap[lab] ?? 0);
    });

    return { labels, matrix };
  }, [scaledGroups]);

  const chartData = useMemo(() => {
    const datasets = scaledGroups.map((g, gi) => ({
      type: "bar",
      label: g.name,
      data: matrix[gi],
      backgroundColor: BAR_COLORS[gi % BAR_COLORS.length],
      borderColor: BAR_COLORS[gi % BAR_COLORS.length],
      borderWidth: 1,
      borderRadius: 4,
      barThickness: 18,
    }));
    return { labels, datasets };
  }, [scaledGroups, labels, matrix]);

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "index", intersect: false },
      plugins: {
        datalabels: {
          anchor: "end",
          align: "end",
          color: "#000",
          font: { weight: "bold", size: 11 },
          formatter: (value) => value.toLocaleString(),
        },
        legend: { position: "bottom", labels: { boxWidth: 12, padding: 12 } },
        tooltip: {
          callbacks: {
            label: function (ctx) {
              const label = ctx.dataset.label || "";
              const value = ctx.parsed.y ?? ctx.parsed.x ?? ctx.raw;
              return `${label}: ${Number(value).toLocaleString()}`;
            },
          },
        },
      },
      scales: {
        x: { stacked: false, ticks: { color: "#475569", font: { size: 12 } } },
        y: {
          beginAtZero: true,
          ticks: {
            color: "#475569",
            callback: function (v) {
              const n = Number(v);
              if (n >= 10000000) return (n / 1000000).toFixed(1) + "M";
              if (n >= 100000) return (n / 1000).toFixed(0) + "k";
              return n.toLocaleString();
            },
          },
          grid: { color: "#f1f5f9" },
        },
      },
      layout: { padding: { top: 8, right: 8, left: 8, bottom: 8 } },
    }),
    []
  );

  return (
    <div className="bg-white rounded-lg shadow-sm px-3 pt-2 pb-0">
      <div className="flex items-center justify-between mb-2">
        <p className="classHeading">Neonatal, Infant & Child Deaths By Cause</p>
      </div>

      <div style={{ height }} className="relative">
        <Bar data={chartData} options={options} plugins={[ChartDataLabels]} />
      </div>
    </div>
  );
}
